import { useEffect, useState } from 'react';
import type { DailyPostDoc } from '../../shared/types';

/**
 * What has been said about a photo, and how much of it you have read.
 *
 * Kept on the device and nowhere else. "New since I looked" is a fact about
 * one person on one phone, and putting it in Firestore would mean a document
 * write every time anybody glanced at anything.
 *
 * Shared by the stories circles and the open photo so the two cannot disagree
 * about whether there is anything left to read — which they did when each kept
 * its own idea of it.
 */
const KEY = 'givisnap:seenTalk';
/*
 * Said on the window whenever the store changes, so the tab dot -- which is
 * drawn far from the sheet that reads the comments -- goes out the moment they
 * are opened rather than on the next unrelated render.
 */
const CHANGED = 'givisnap:seenTalkChanged';

/*
 * Counters written before this meant reactions plus replies, so they are at
 * least as large as the replies they now stand for. Reading one back can only
 * under-report what is unread, for one viewing, which is the gentler way round
 * to be wrong — and it settles itself the moment the photo is opened.
 */

export type SeenTalk = Record<string, number>;

/**
 * What has been *said* about a post — replies, and nothing else.
 *
 * Reactions used to be counted in here too, which put them behind the comment
 * icon: somebody tapped a heart and the photo grew a badge that promised
 * unread comments there were none of. A reaction is not something you read,
 * and it is already on screen as its own chip, so it is no longer part of this
 * number at all.
 *
 * Your own replies cannot be excluded by uid — replyCount is one number on the
 * post and does not say who wrote them — so those are settled at the moment of
 * writing, by creditOwnReply below.
 */
export function repliesOf(post: DailyPostDoc): number {
  return post.replyCount ?? 0;
}

export function readSeenTalk(): SeenTalk {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as SeenTalk;
  } catch {
    return {};
  }
}

function write(next: SeenTalk): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private mode or blocked storage. Everything then reads as unread, which
    // is a worse screen and not a broken one.
  }
  window.dispatchEvent(new Event(CHANGED));
}

/** The store, kept current as comments are opened here or in another tab. */
export function useSeenTalk(): SeenTalk {
  const [seen, setSeen] = useState<SeenTalk>(readSeenTalk);
  useEffect(() => {
    const reread = () => setSeen(readSeenTalk());
    window.addEventListener(CHANGED, reread);
    window.addEventListener('storage', reread);
    return () => {
      window.removeEventListener(CHANGED, reread);
      window.removeEventListener('storage', reread);
    };
  }, []);
  return seen;
}

/** How much of this post's talk you have not seen yet. */
export function unreadOf(post: { id: string; data: DailyPostDoc }, seen: SeenTalk): number {
  return Math.max(0, repliesOf(post.data) - (seen[post.id] ?? 0));
}

/**
 * Whether this is yours and somebody has commented on it since you last
 * opened its comments. Only your own: on everyone else's the comments are
 * there to read when you like, and lighting each of those would make the mark
 * mean nothing.
 */
export function waitingOnMe(
  post: { id: string; data: DailyPostDoc },
  uid: string,
  seen: SeenTalk,
): boolean {
  return post.data.authorUid === uid && unreadOf(post, seen) > 0;
}

/**
 * Catch up on one post. Returns the whole store so a caller holding it in
 * state can keep up without reading the device back.
 */
export function markTalkRead(post: { id: string; data: DailyPostDoc }): SeenTalk {
  const next = { ...readSeenTalk(), [post.id]: repliesOf(post.data) };
  write(next);
  return next;
}

/**
 * Account for a reply you just wrote.
 *
 * The count on the post is raised a moment later by a trigger, and it cannot
 * tell your replies from anyone else's — so left alone, writing a comment
 * announces itself back to you as something unread. Claiming the increment now
 * keeps the two in step whichever order they land in: the post has certainly
 * not been counted yet at the moment of writing, because the write is what
 * starts the counting.
 */
export function creditOwnReply(post: { id: string; data: DailyPostDoc }): SeenTalk {
  const store = readSeenTalk();
  const base = store[post.id] ?? repliesOf(post.data);
  const next = { ...store, [post.id]: base + 1 };
  write(next);
  return next;
}
